// NEW_FILE_JUSTIFIED: Email thread view component — displays email chains with inbound/outbound messages. No existing component handles this (ChatPanel is for Telegram chat bubbles).
import { useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Reply,
  Forward,
  Download,
  FileText,
  Image as ImageIcon, // AS_JUSTIFIED: lucide-react named export aliased to avoid conflict with HTML Image element
  FileSpreadsheet,
  File,
  UserPlus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AiReplyButton } from '@/components/aiReply/AiReplyButton';
import { AiDraftBubble } from '@/components/aiReply/AiDraftBubble';
import { useAiReplyDrafts } from '@/hooks/useAiReplyDrafts';
import { useAction } from 'convex/react';
import { api } from '@convex/_generated/api';
import { toast } from 'sonner';
import type { IEmailThreadProps, IGmailAttachment, IEmailMessageCardProps } from '@/@types/gmail';

// ============================================================================
// Pure helpers
// ============================================================================

/** Format timestamp for email header display */
function formatFullDate(timestampMs: number): string {
  const date = new Date(timestampMs);
  return (
    date.toLocaleDateString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) +
    ' at ' +
    date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    })
  );
}

/** Extract display name from "Name <email@example.com>" format */
function extractName(fullAddress: string): string {
  const match = fullAddress.match(/^([^<]+)</);
  if (match) return match[1].trim();
  return fullAddress.split('@')[0] ?? fullAddress;
}

/** Format file size for display */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Get file type display info based on MIME type */
function getFileTypeInfo(mimeType: string) {
  if (mimeType === 'application/pdf')
    return {
      Icon: FileText,
      label: 'PDF',
      accent: 'bg-red-500',
      bgColor: 'bg-red-50 dark:bg-red-950/30',
    };
  if (mimeType.startsWith('image/'))
    return {
      Icon: ImageIcon,
      label: 'IMG',
      accent: 'bg-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    };
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv'))
    return {
      Icon: FileSpreadsheet,
      label: 'XLS',
      accent: 'bg-green-500',
      bgColor: 'bg-green-50 dark:bg-green-950/30',
    };
  if (mimeType.includes('document') || mimeType.includes('word') || mimeType.includes('msword'))
    return {
      Icon: FileText,
      label: 'DOC',
      accent: 'bg-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    };
  return { Icon: File, label: 'FILE', accent: 'bg-muted-foreground', bgColor: 'bg-muted/30' };
}

// ============================================================================
// Sub-components
// ============================================================================

function EmailMessageCard({ message, isLast, onDownloadAttachment }: IEmailMessageCardProps) {
  const isOutgoing = message.direction === 'outgoing';

  // Some emails store HTML in bodyText when bodyHtml is absent
  const htmlSource =
    message.bodyHtml ??
    (message.bodyText && /<[a-z][\s\S]*>/i.test(message.bodyText) ? message.bodyText : '');

  const sanitizedHtml = DOMPurify.sanitize(htmlSource, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['script', 'style', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  });

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card p-4',
        isLast && 'ring-1 ring-primary/20'
      )}
    >
      {/* Message Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
                isOutgoing
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              {isOutgoing ? 'Sent' : 'Received'}
            </span>
            <span className="text-sm font-medium text-foreground truncate">
              {extractName(message.from)}
            </span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            <span>To: {message.to.map(extractName).join(', ')}</span>
            {message.cc && message.cc.length > 0 && (
              <span className="ml-2">CC: {message.cc.map(extractName).join(', ')}</span>
            )}
          </div>
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatFullDate(message.internalDate)}
        </span>
      </div>

      <Separator className="mb-3" />

      {/* Message Body */}
      <div className="prose prose-sm dark:prose-invert max-w-none overflow-hidden">
        {htmlSource ? (
          <div
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            className="text-sm [&_img]:max-w-full [&_a]:text-link [&_a]:underline"
          />
        ) : (
          <pre className="text-sm whitespace-pre-wrap font-sans text-foreground">
            {message.bodyText ?? message.snippet}
          </pre>
        )}
      </div>

      {/* Attachments — Gmail-style preview cards */}
      {message.hasAttachments && message.attachments.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-foreground">
              {message.attachments.length === 1
                ? 'One attachment'
                : `${message.attachments.length} attachments`}
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            {message.attachments.map((att: IGmailAttachment) => {
              const { Icon, label, accent, bgColor } = getFileTypeInfo(att.mimeType);
              return (
                <button
                  key={att.attachmentId}
                  type="button"
                  onClick={() =>
                    onDownloadAttachment(message.gmailMessageId, att.attachmentId, att.filename)
                  }
                  className="group flex flex-col w-52 rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow text-left"
                  aria-label={`Download ${att.filename} (${formatFileSize(att.size)})`}
                >
                  <div
                    className={cn('h-28 flex flex-col items-center justify-center gap-1', bgColor)}
                  >
                    <Icon size={36} className="text-muted-foreground/60" aria-hidden="true" />
                    <span className="text-xs text-muted-foreground">
                      {formatFileSize(att.size)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
                    <div
                      className={cn(
                        'w-5 h-5 rounded flex items-center justify-center shrink-0',
                        accent
                      )}
                    >
                      <span className="text-[8px] font-bold text-white leading-none">{label}</span>
                    </div>
                    <span className="text-xs truncate text-foreground">{att.filename}</span>
                    <Download
                      size={14}
                      className="ml-auto shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function EmailThread({
  thread,
  onBack,
  onReply,
  onForward,
  onDownloadAttachment,
  onCreateLead,
  contactIdentifier,
  contactName,
  onEditAndSendDraft,
  onExtractLeads,
  canWrite = true,
}: IEmailThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Scroll to the latest message when thread loads or message count changes
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread.messages.length]);

  const { pendingDraft, handleCancel, handleMarkSent } = useAiReplyDrafts(
    contactIdentifier,
    'gmail'
  );
  const sendEmail = useAction(api.gmail.actions.sendEmail);

  const handleDraftSend = useCallback(
    async (draftId: Parameters<typeof handleMarkSent>[0], text: string) => {
      if (!contactIdentifier) return;
      try {
        const lastMessage = thread.messages[thread.messages.length - 1];
        await sendEmail({
          to: [contactIdentifier],
          subject: thread.subject.startsWith('Re:') ? thread.subject : `Re: ${thread.subject}`,
          bodyHtml: `<p>${text.replace(/\n/g, '<br/>')}</p>`,
          threadId: thread.threadId,
          inReplyTo: lastMessage?.gmailMessageId,
        });
        await handleMarkSent(draftId, text);
        toast.success('Email sent');
      } catch {
        toast.error('Failed to send email');
      }
    },
    [contactIdentifier, thread, sendEmail, handleMarkSent]
  );

  const handleDraftEditAndSend = useCallback(
    (draftText: string) => {
      if (onEditAndSendDraft) {
        const subject = thread.subject.startsWith('Re:') ? thread.subject : `Re: ${thread.subject}`;
        onEditAndSendDraft(draftText, subject);
      }
    },
    [onEditAndSendDraft, thread.subject]
  );

  const handleCreateLead = () => {
    if (onExtractLeads) {
      onExtractLeads({
        threadId: thread.threadId,
        subject: thread.subject,
        messages: thread.messages.map(m => ({
          from: m.from,
          bodyText: m.bodyText,
          snippet: m.snippet,
          direction: m.direction,
        })),
      });
      return;
    }
    // Fallback to single-lead extraction
    if (!onCreateLead) return;
    const incoming = thread.messages.find(m => m.direction === 'incoming');
    const msg = incoming ?? thread.messages[0];
    if (msg) {
      const nameMatch = msg.from.match(/^([^<]+)</);
      const name = nameMatch ? nameMatch[1].trim() : (msg.from.split('@')[0] ?? '');
      const emailAddr = msg.from.match(/<([^>]+)>/)?.[1] ?? msg.from;
      const bodyText = thread.messages
        .map(m => m.bodyText ?? m.snippet)
        .filter(Boolean)
        .join('\n\n---\n\n');
      onCreateLead(name, emailAddr, thread.subject, bodyText);
    }
  };

  return (
    <div className="flex flex-col min-h-full">
      {/* Thread Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3 sticky top-0 z-10 bg-background">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back to email list">
            <ArrowLeft size={16} aria-hidden="true" />
          </Button>
          <h2 className="text-lg font-semibold text-foreground truncate">
            {thread.subject || '(no subject)'}
          </h2>
          <span className="text-xs text-muted-foreground shrink-0">
            {thread.messages.length} message{thread.messages.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {canWrite && contactIdentifier && (
            <AiReplyButton
              contactIdentifier={contactIdentifier}
              platform="gmail"
              contactName={contactName}
            />
          )}
          {(onExtractLeads ?? onCreateLead) && (
            <Button variant="secondary" size="sm" onClick={handleCreateLead}>
              <UserPlus size={14} className="mr-1" aria-hidden="true" />
              Create Lead
            </Button>
          )}
          {canWrite && (
            <>
              <Button variant="ghost" size="sm" onClick={onReply}>
                <Reply size={14} className="mr-1" aria-hidden="true" />
                Reply
              </Button>
              <Button variant="ghost" size="sm" onClick={onForward}>
                <Forward size={14} className="mr-1" aria-hidden="true" />
                Forward
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Message Thread */}
      <div className="flex flex-col gap-3 p-4 flex-1">
        {canWrite && pendingDraft !== null && (
          <AiDraftBubble
            draft={pendingDraft}
            onSend={handleDraftSend}
            onCancel={handleCancel}
            onEditAndSend={onEditAndSendDraft ? handleDraftEditAndSend : undefined}
          />
        )}
        {thread.messages.map((message, index) => (
          <EmailMessageCard
            key={message.id}
            message={message}
            isLast={index === thread.messages.length - 1}
            onDownloadAttachment={onDownloadAttachment}
          />
        ))}
        <div ref={bottomRef} aria-hidden="true" />
      </div>

      {/* Bottom Action Buttons — sticky footer like Gmail */}
      {canWrite && (
        <div className="flex items-center gap-3 px-4 py-3 border-t border-border sticky bottom-0 bg-background">
          <Button variant="outline" className="rounded-full px-5" onClick={onReply}>
            <Reply size={16} className="mr-2" aria-hidden="true" />
            Reply
          </Button>
          <Button variant="outline" className="rounded-full px-5" onClick={onForward}>
            <Forward size={16} className="mr-2" aria-hidden="true" />
            Forward
          </Button>
        </div>
      )}
    </div>
  );
}
